import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  CURRENCY_STORAGE_KEY,
  DEFAULT_CURRENCY,
  FALLBACK_RATES,
  SUPPORTED_CURRENCIES,
  convertAmount,
  formatCurrency,
  normalizeCurrencyCode,
} from '../lib/currency';

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    try {
      return normalizeCurrencyCode(localStorage.getItem(CURRENCY_STORAGE_KEY));
    } catch {
      return DEFAULT_CURRENCY;
    }
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, selectedCurrency);
    } catch {
      // Ignore storage errors.
    }
  }, [selectedCurrency]);

  useEffect(() => {
    let cancelled = false;
    const fetchRates = async () => {
      setRatesLoading(true);
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/GBP');
        const data = await response.json();
        if (!cancelled && data?.result === 'success' && data?.rates) {
          const mergedRates = { ...FALLBACK_RATES, ...data.rates };
          setRates(mergedRates);
          setLastUpdated(data.time_last_update_utc || new Date().toISOString());
        }
      } catch {
        if (!cancelled) {
          setRates(FALLBACK_RATES);
        }
      } finally {
        if (!cancelled) {
          setRatesLoading(false);
        }
      }
    };

    fetchRates();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      selectedCurrency,
      setSelectedCurrency: (code) => setSelectedCurrency(normalizeCurrencyCode(code)),
      rates,
      ratesLoading,
      lastUpdated,
      supportedCurrencies: SUPPORTED_CURRENCIES,
      convert: (amount, fromCurrency = DEFAULT_CURRENCY) =>
        convertAmount(amount, fromCurrency, selectedCurrency, rates),
      format: (amount, currencyCode = selectedCurrency) => formatCurrency(amount, currencyCode),
      formatFrom: (amount, fromCurrency = DEFAULT_CURRENCY) =>
        formatCurrency(convertAmount(amount, fromCurrency, selectedCurrency, rates), selectedCurrency),
    }),
    [selectedCurrency, rates, ratesLoading, lastUpdated]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return ctx;
};
